import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { FormPanel } from '../../components/FormPanel';
import { KpiCard } from '../../components/KpiCard';
import { Select } from '../../components/Select';
import { listBranches } from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import {
  branchDisplayLabel,
  deviceDisplayLabel,
} from '../../lib/displayIdentity';
import { fmtInt } from '../../lib/format';
import { useUi } from '../../stores/ui';
import { OperationsHeader } from './OperationsHeader';

function fmtDateTime(value?: string): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('th-TH', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function SyncHealthView() {
  const branchId = useUi((state) => state.branchId);
  const branchesQuery = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const branches = branchesQuery.data ?? [];
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const deviceHealth = useQuery({
    queryKey: ['device-health', branchId],
    queryFn: () =>
      dashboardApi.getDeviceHealth({
        branchId: branchId ?? undefined,
      }),
  });

  useEffect(() => {
    const firstDeviceId = deviceHealth.data?.devices[0]?.deviceId ?? null;
    setSelectedDeviceId((current) =>
      current &&
      deviceHealth.data?.devices.some((device) => device.deviceId === current)
        ? current
        : firstDeviceId,
    );
  }, [deviceHealth.data]);

  const syncHealth = useQuery({
    queryKey: ['sync-health', selectedDeviceId, branchId],
    queryFn: () =>
      dashboardApi.getSyncHealthReport({
        deviceId: selectedDeviceId!,
        branchId: branchId ?? undefined,
      }),
    enabled: !!selectedDeviceId,
  });
  const issueCount =
    (syncHealth.data?.failedCount ?? 0) + (syncHealth.data?.blockedCount ?? 0);

  return (
    <div className="bo-fade">
      <OperationsHeader
        title="สถานะซิงก์"
        subtitle="ติดตามคิวข้อมูลของแต่ละเครื่อง เห็นรายการที่ติดค้าง และหาสาเหตุก่อนข้อมูลข้ามสาขาจะคลาดเคลื่อน"
        status={
          !selectedDeviceId || syncHealth.isLoading
            ? { label: 'รอข้อมูลสถานะ', tone: 'neutral' }
            : issueCount > 0
              ? {
                  label: `พบ ${fmtInt(issueCount)} รายการที่ต้องดูแล`,
                  tone: syncHealth.data?.failedCount ? 'danger' : 'warn',
                }
              : { label: 'ซิงก์เป็นปกติ', tone: 'ok' }
        }
      />
      {branchesQuery.isError && (
        <p role="alert" className="mb-4 text-sm-label">
          โหลดรายชื่อสาขาไม่สำเร็จ
        </p>
      )}
      <FormPanel className="mb-6 flex flex-col gap-4 md:flex-row md:items-end">
        <Field label="เลือกเครื่อง" className="w-full md:max-w-sm">
          <Select
            aria-label="เลือกเครื่อง"
            value={selectedDeviceId ?? ''}
            onChange={(event) =>
              setSelectedDeviceId(event.target.value || null)
            }
          >
            {(deviceHealth.data?.devices ?? []).map((device) => {
              const branch = branches.find(
                (branch) => branch.id === device.branchId,
              );
              const branchLabel = branch
                ? branchDisplayLabel(branch)
                : branchesQuery.isPending
                  ? 'กำลังโหลดสาขา'
                  : branchesQuery.isError
                    ? 'โหลดสาขาไม่สำเร็จ'
                    : branchDisplayLabel();
              return (
                <option key={device.deviceId} value={device.deviceId}>
                  {deviceDisplayLabel(device)} · {branchLabel}
                </option>
              );
            })}
          </Select>
        </Field>
        {selectedDeviceId && (
          <div className="text-sm-label text-ink-3 md:pb-2.5">
            กำลังดูคิวข้อมูลของ{' '}
            {deviceDisplayLabel(
              deviceHealth.data?.devices.find(
                (device) => device.deviceId === selectedDeviceId,
              ),
            )}
          </div>
        )}
      </FormPanel>

      {!selectedDeviceId ? (
        <div className="bo-card">
          <EmptyState
            title="ยังไม่มีเครื่องในสาขาที่ดูได้"
            sub="ต้องมีเครื่องในหน้าเครื่อง POS ก่อน จึงจะดูสถานะซิงก์ได้"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <KpiCard
              label="รอส่ง"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.pendingCount ?? 0)
              }
              tone="warn"
              sub="รายการที่ยังอยู่ในคิว"
            />
            <KpiCard
              label="ส่งไม่สำเร็จ"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.failedCount ?? 0)
              }
              tone="danger"
              sub="รายการที่ต้องตรวจสอบ"
            />
            <KpiCard
              label="ลำดับติดค้าง"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.blockedCount ?? 0)
              }
              tone="warn"
              sub="รอข้อมูลลำดับก่อนหน้า"
            />
            <KpiCard
              label="ลำดับล่าสุด"
              value={
                syncHealth.isLoading
                  ? '…'
                  : fmtInt(syncHealth.data?.lastAppliedSequence ?? 0)
              }
              sub={fmtDateTime(syncHealth.data?.lastAppliedAt)}
            />
          </div>
          <div className="grid gap-6 xl:grid-cols-2">
            <Card title="รายการลำดับที่ติดค้าง">
              <div className="overflow-x-auto">
                <table className="bo-table">
                  <thead>
                    <tr>
                      <th>ลำดับ</th>
                      <th>เหตุการณ์</th>
                      <th>ข้อมูล</th>
                      <th>ติดค้างเมื่อ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(syncHealth.data?.gaps ?? []).map((gap) => (
                      <tr key={gap.eventId}>
                        <td className="num">{fmtInt(gap.eventSequence)}</td>
                        <td>{gap.entityType}</td>
                        <td>
                          <details>
                            <summary className="cursor-pointer">
                              ดูรหัสอ้างอิง
                            </summary>
                            <code className="break-all">{gap.entityId}</code>
                          </details>
                        </td>
                        <td>{fmtDateTime(gap.createdAt)}</td>
                      </tr>
                    ))}
                    {(syncHealth.data?.gaps ?? []).length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-ink-3 py-4">
                          ไม่มีลำดับติดค้าง
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card title="รายการที่ส่งไม่สำเร็จ">
              <div className="overflow-x-auto">
                <table className="bo-table">
                  <thead>
                    <tr>
                      <th>ลำดับ</th>
                      <th>เหตุการณ์</th>
                      <th>ข้อผิดพลาด</th>
                      <th>สถานะ</th>
                      <th>จำนวนครั้งที่ลอง</th>
                      <th>ล่าสุด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(syncHealth.data?.dlq ?? []).map((entry) => (
                      <tr key={entry.eventId}>
                        <td className="num">{fmtInt(entry.eventSequence)}</td>
                        <td>{entry.entityType}</td>
                        <td>{entry.lastError ?? '—'}</td>
                        <td>{entry.resolutionStatus ?? '—'}</td>
                        <td className="num">{fmtInt(entry.retryCount)}</td>
                        <td>{fmtDateTime(entry.lastAttemptedAt)}</td>
                      </tr>
                    ))}
                    {(syncHealth.data?.dlq ?? []).length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-ink-3 py-4">
                          ไม่มีรายการส่งไม่สำเร็จ
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
